import { initialState as userInitialState, UserState } from "src/app/store/user/user.state";
import { persistedKeys as app } from "src/app/store/app/app.state";
import { persistedKeys as coupon, CouponTableHeader } from "./store/coupon/coupon.state";
import { persistedKeys as program } from "./store/program/program.state";
import { persistedKeys as workflow } from "./store/workflow/workflow.state";
import { persistedKeys as toom } from "./store/toom/toom.state";
import { persistedKeys as newsfeed, PostTableHeader } from "./store/newsfeed/newsfeed.state";
import { persistedKeys as webhook } from "./store/webhook/webhook.state";
import { persistedKeys as accountIdentifier } from "./store/accountidentifier/accountidentifier.state";
import { CouponTableHeader as CustomerCouponTableHeader, persistedKeys as customer } from "./store/customer/customer.state";
import { KpiHeaders } from "./store/newsfeed/interfaces/kpis.interface";

import { ApiModule as CouponApiModule, Configuration } from "@api/coupons";
import { ApiModule as ProgramApiModule } from "@api/programs";
import { ApiModule as ReportApiModule } from "@api/reports-v3";
import { ApiModule as WorkflowApiModule } from "@api/workflow-service";
import { ApiModule as SearchApiModule } from "@api/search";
import { ApiModule as TrackingApiModule } from "@api/tracking";
import { ApiModule as NewsfeedApiModule } from "@api/newsfeed";
import { ApiModule as PushNotificationApiModule } from "@api/pushnotification";
import { ApiModule as WebhookApiModule } from "@api/webhooks";
import { ApiModule as AccountIdentifiersApiModule } from "@api/accountidentifiers";
import { ApiModule as UsersApiModule } from "@api/users";
import { ApiModule as AccountsApiModule } from "@api/accounts";
import { ApiModule as TransactionsApiModule } from "@api/transactions";
import { ApiModule as UserAppServiceApiModule } from "@api/user-app-service";
import { ApiModule as BiServiceApiModule, Configuration as BiServiceConfiguration } from "@api/bi-service";
import { ApiModule as EventApiModule } from "@api/event";
import { ApiModule as BonscanApiModule, Configuration as BonscanConfiguration } from "@api/bonscan";
import { ApiModule as BrazeServiceApiModule } from "@api/braze-service";
import { ApiModule as AgentApiModule, Configuration as AgentConfiguration } from "@api/agent";
import { ApiModule as ConnectorCorrelationServiceApiModule } from "@api/connector-correlation-service";
import { ApiModule as WalletPassServiceApiModule } from "@api/wallet-pass-service";

import localeEn from "@angular/common/locales/en";
import localeDe from "@angular/common/locales/de";
import localeCs from "@angular/common/locales/cs";
import localeSk from "@angular/common/locales/sk";
import localePl from "@angular/common/locales/pl";
import localeHu from "@angular/common/locales/hu";
import localeIt from "@angular/common/locales/it";
import localeEs from "@angular/common/locales/es";

import * as Sentry from "@sentry/angular";
import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import { BrowserModule } from "@angular/platform-browser";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Action, ActionReducer, MetaReducer, Store, StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { environment } from "../environments/environment";
import { EffectsModule } from "@ngrx/effects";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { effects, reducers, RootState } from "./store";
import { localStorageSync } from "ngrx-store-localstorage";
import { initializeApp } from "./app.initializer.factory";
import { AuthInterceptor } from "./interceptors/auth/auth-interceptor";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { LoaderInterceptor } from "./interceptors/loader/loader.interceptor";
import { ToomInterceptor } from "./interceptors/auth/toom.interceptor";
import { TranslateModule, TranslateModuleConfig, TranslateLoader } from "@ngx-translate/core";
import { createTranslateLoader } from "./services/i18n/create-translate-loader";
import { Router } from "@angular/router";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "./constants";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from "@angular/material/snack-bar";
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";
import { DatePipe, registerLocaleData } from "@angular/common";
import hljs from "highlight.js/lib/core";
import json from "highlight.js/lib/languages/json";
import { NotFoundInterceptor } from "./interceptors/not-found/not-found-interceptor";
import { ErrorNotificationInterceptor } from "./interceptors/error-notification/error-notification.interceptor";
import { ProgramHeaderInterceptor } from "./interceptors/program-header/program-header.interceptor";
import { WalletPassLanguageInterceptor } from "./interceptors/wallet-pass-language/wallet-pass-language.interceptor";
import { WalletPassPreviewMockInterceptor } from "./interceptors/wallet-pass-preview-mock/wallet-pass-preview-mock.interceptor";
import { provideNativeDateAdapter } from "@angular/material/core";
import { ServiceWorkerModule } from "@angular/service-worker";
import { GlobalErrorHandler } from "./error-handlers/global-error-handler";
import { ShowWhenLoadingDirective } from "./directives/show-when-loading/show-when-loading.directive";
import { getSelectedProgramId } from "./utils/selected-program-id";
import { hydrateUserAuthForCurrentProgram, pickPersistedUserState } from "./utils/user-local-storage";

hydrateUserAuthForCurrentProgram();

registerLocaleData(localeEn);
registerLocaleData(localeDe);
registerLocaleData(localeCs);
registerLocaleData(localeSk);
registerLocaleData(localePl);
registerLocaleData(localeHu);
registerLocaleData(localeIt);
registerLocaleData(localeEs);

export const translateModuleConfig: () => TranslateModuleConfig = () => {
  const browserLang = navigator.language.split("-")[0];
  return {
    defaultLanguage: AVAILABLE_LANGUAGES.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE,
    loader: {
      provide: TranslateLoader,
      useFactory: createTranslateLoader,
      deps: [HttpClient],
    },
  };
};

const processCouponHeaders = (headers: CouponTableHeader[]): CouponTableHeader[] => {
  const filtered = headers.filter(header => header !== "actions");
  return filtered.includes("name") ? filtered : ["name", ...filtered];
};

const processCustomerCouponsHeaders = (headers: CustomerCouponTableHeader[]): CustomerCouponTableHeader[] => {
  const filtered = headers.filter(header => header !== "i18nFields");
  return filtered.includes("name") ? filtered : ["name", ...filtered];
};

const processNewsfeedHeaders = (headers: PostTableHeader[]): PostTableHeader[] => {
  // `pushStatus` was dropped in CNG-19706 but may still be persisted, and an unknown column breaks the table.
  const filtered = headers.filter(header => header !== "count" && header !== "actions" && (header as string) !== "pushStatus");
  const includesKpiHeaders = KpiHeaders.every(header => filtered.includes(header));
  return includesKpiHeaders ? filtered : [...filtered, ...KpiHeaders];
};

const migrateState = (state: RootState): RootState => {
  if (!state.coupon?.selectedHeaders) return state;

  return {
    ...state,
    coupon: {
      ...state.coupon,
      selectedHeaders: processCouponHeaders(state.coupon.selectedHeaders),
    },
    customer: {
      ...state.customer,
      selectedHeadersCoupons: processCustomerCouponsHeaders(state.customer.selectedHeadersCoupons),
    },
    newsfeed: {
      ...state.newsfeed,
      selectedPostHeaders: processNewsfeedHeaders(state.newsfeed.selectedPostHeaders),
    },
  };
};

const needsMigration = (state: RootState): boolean => {
  const couponHeaders = state?.coupon?.selectedHeaders;
  const couponNeedsMigration = couponHeaders ? couponHeaders.includes("actions") || !couponHeaders.includes("name") : false;

  const customerCouponHeaders = state?.customer?.selectedHeadersCoupons;
  const customerCouponNeedsMigration = customerCouponHeaders
    ? customerCouponHeaders.includes("i18nFields") || !customerCouponHeaders.includes("name")
    : false;

  const newsfeedHeaders = state?.newsfeed?.selectedPostHeaders;
  const newsfeedNeedsMigration =
    !newsfeedHeaders?.some(header => (KpiHeaders as PostTableHeader[]).includes(header)) ||
    (newsfeedHeaders as string[]).includes("pushStatus");

  return couponNeedsMigration || customerCouponNeedsMigration || newsfeedNeedsMigration;
};

export const migrationMetaReducer: MetaReducer<RootState> = (reducer: ActionReducer<RootState>) => (state, action) => {
  const currentState = state && needsMigration(state) ? migrateState(state) : state;
  return reducer(currentState, action);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const localStorageReduxSync = (reducer: ActionReducer<RootState>): ActionReducer<any> =>
  localStorageSync({
    keys: [
      { app },
      { coupon },
      { program },
      {
        user: {
          serialize: (state: UserState) => pickPersistedUserState(state),
          deserialize: (stored: Partial<UserState> | undefined): UserState | Partial<UserState> | undefined => {
            if (stored?.accessToken) {
              return { ...userInitialState, ...stored };
            }

            const migrated = hydrateUserAuthForCurrentProgram();
            if (migrated) {
              return { ...userInitialState, ...migrated };
            }

            return stored ? { ...userInitialState, ...stored } : stored;
          },
        },
      },
      { workflow },
      { toom },
      { newsfeed },
      { accountIdentifier },
      { webhook },
      { customer },
    ],
    rehydrate: true,
    restoreDates: false,
    storageKeySerializer: key => `${getSelectedProgramId()}_selfservice_${environment.name}_${key}`,
  })(reducer);

const metaReducers: MetaReducer<RootState, Action>[] = [migrationMetaReducer, localStorageReduxSync];

export const setApiConfig = (): Configuration => new Configuration({ basePath: environment.baseApi });

// Generated clients each seed their own credential map, so each needs its own Configuration class.
export const setBiServiceApiConfig = (): BiServiceConfiguration => new BiServiceConfiguration({ basePath: environment.baseApi });

export const setBonscanApiConfig = (): BonscanConfiguration => new BonscanConfiguration({ basePath: environment.clarificationApi });

export const setAgentApiConfig = (): AgentConfiguration => new AgentConfiguration({ basePath: environment.baseApi });

hljs.registerLanguage("json", json);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.name === "production", connectInZone: false }),
    EffectsModule.forRoot(effects),
    StoreRouterConnectingModule.forRoot(),
    TranslateModule.forRoot(translateModuleConfig()),
    CouponApiModule.forRoot(setApiConfig),
    ProgramApiModule.forRoot(setApiConfig),
    ReportApiModule.forRoot(setApiConfig),
    WorkflowApiModule.forRoot(setApiConfig),
    SearchApiModule.forRoot(setApiConfig),
    TrackingApiModule.forRoot(setApiConfig),
    NewsfeedApiModule.forRoot(setApiConfig),
    PushNotificationApiModule.forRoot(setApiConfig),
    WebhookApiModule.forRoot(setApiConfig),
    AccountIdentifiersApiModule.forRoot(setApiConfig),
    UsersApiModule.forRoot(setApiConfig),
    AccountsApiModule.forRoot(setApiConfig),
    TransactionsApiModule.forRoot(setApiConfig),
    UserAppServiceApiModule.forRoot(setApiConfig),
    BiServiceApiModule.forRoot(setBiServiceApiConfig),
    EventApiModule.forRoot(setApiConfig),
    BonscanApiModule.forRoot(setBonscanApiConfig),
    BrazeServiceApiModule.forRoot(setApiConfig),
    AgentApiModule.forRoot(setAgentApiConfig),
    ConnectorCorrelationServiceApiModule.forRoot(setApiConfig),
    WalletPassServiceApiModule.forRoot(setApiConfig),
    MatSnackBarModule,
    MatProgressBarModule,
    MatDialogModule,
    ServiceWorkerModule.register("ngsw-worker.js", { enabled: environment.name !== "development" }),
    ShowWhenLoadingDirective,
  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [Store], multi: true },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    { provide: APP_INITIALIZER, useFactory: () => (): void => {}, deps: [Sentry.TraceService], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ProgramHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WalletPassLanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WalletPassPreviewMockInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ToomInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NotFoundInterceptor, multi: true },
    // Keep last: needs the raw HttpErrorResponse, before NotFoundInterceptor rethrows only its body.
    { provide: HTTP_INTERCEPTORS, useClass: ErrorNotificationInterceptor, multi: true },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "fill", floatLabel: "always", subscriptSizing: "dynamic" } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000, horizontalPosition: "center", verticalPosition: "bottom" } },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { autoFocus: false } },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: Sentry.TraceService, deps: [Router] },
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideNativeDateAdapter(),
    DatePipe,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export { AppComponent } from "./app.component";
